export type AxisAlign = (typeof AxisAlign)[keyof typeof AxisAlign];
export const AxisAlign = {
    Center: 'center',
    Start: 'start',
    End: 'end',
    Nearest: 'nearest',
} as const;

export interface ScrollOptions {
    /** Horizontal alignment when scrolling x-axis */
    align?: AxisAlign;
    /** When true, also scroll along the y-axis */
    includeVertical?: boolean;
    /** Vertical alignment when scrolling y-axis */
    verticalAlign?: AxisAlign;
    /**
     * Controls when to perform scrolling relative to current visibility:
     * - true: only scroll axes that are not fully visible (both axes considered independently)
     * - false: always scroll included axes regardless of current visibility
     * - 'horizontally': apply the "only-if-not-visible" rule to horizontal only; vertical always scrolls when included
     * - 'vertically': apply the "only-if-not-visible" rule to vertical only; horizontal always scrolls
     */
    onlyIfNotFullyVisible?: boolean | 'horizontally' | 'vertically';
    /** Scroll behavior */
    behavior?: ScrollBehavior; // 'auto' | 'smooth'
    callback?: () => void;
}

const EPSILON_PX = 1;

/**
 * Scrolls the target element horizontally into view within the container without affecting vertical scroll.
 * Optionally disables scroll snap temporarily to avoid browser auto-snapping during programmatic scroll.
 */
/**
 * Programmatically scroll a target element into view within a scrollable container.
 * Performs horizontal scrolling and, optionally, vertical scrolling without relying on Element.scrollIntoView,
 * preserving control over alignment and scroll-snap behavior.
 *
 * @param container The scrollable container element (must be the element whose scrollLeft/scrollTop should change).
 * @param target The element to bring into view (must be a descendant of the container).
 * @param options Scroll options controlling alignment, axes, and when to perform scrolling.
 *  - align: horizontal alignment (default: 'center')
 *  - includeVertical: whether to also scroll vertically (default: false)
 *  - verticalAlign: vertical alignment when includeVertical is true (default: 'nearest')
 *  - onlyIfNotFullyVisible:
 *      true: only scroll an axis if it is not fully visible (per-axis rule)
 *      false: always scroll included axes
 *      'horizontally': apply the rule only to horizontal; vertical always scrolls when included
 *      'vertically': apply the rule only to vertical; horizontal always scrolls
 *  - behavior: 'auto' | 'smooth' (default: 'smooth')
 */
export function scrollIntoView(
    container: HTMLDivElement,
    target: HTMLElement,
    {
        align = AxisAlign.Center,
        includeVertical = false,
        verticalAlign = AxisAlign.Nearest,
        onlyIfNotFullyVisible = true,
        behavior = 'smooth',
        callback,
    }: ScrollOptions = {}
): void {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const isHorizVis =
        targetRect.left >= containerRect.left - EPSILON_PX &&
        targetRect.right <= containerRect.right + EPSILON_PX;
    const isVertVis =
        targetRect.top >= containerRect.top - EPSILON_PX &&
        targetRect.bottom <= containerRect.bottom + EPSILON_PX;

    const applyOnlyIfNotHorizontal =
        onlyIfNotFullyVisible === true ||
        onlyIfNotFullyVisible === 'horizontally';
    const applyOnlyIfNotVertical =
        includeVertical &&
        (onlyIfNotFullyVisible === true ||
            onlyIfNotFullyVisible === 'vertically');

    // Compute desired offsets per axis
    const desiredLeft = computeDesiredAxisScroll(
        'x',
        container,
        target,
        containerRect,
        targetRect,
        align,
        applyOnlyIfNotHorizontal && isHorizVis
    );

    const desiredTop = includeVertical
        ? computeDesiredAxisScroll(
              'y',
              container,
              target,
              containerRect,
              targetRect,
              verticalAlign,
              applyOnlyIfNotVertical && isVertVis
          )
        : container.scrollTop;

    const style = container.style;
    const previousSnapType = style.scrollSnapType;
    style.scrollSnapType = 'initial';

    container.scrollTo({ left: desiredLeft, top: desiredTop, behavior });

    requestAnimationFrame(() => {
        style.scrollSnapType = previousSnapType || 'x mandatory';
        callback?.();
    });
}

function computeDesiredAxisScroll(
    axis: 'x' | 'y',
    container: HTMLDivElement,
    target: HTMLElement,
    containerRect: DOMRect,
    targetRect: DOMRect,
    align: AxisAlign,
    skip: boolean
): number {
    if (skip) {
        return axis === 'x' ? container.scrollLeft : container.scrollTop;
    }

    const startProp = axis === 'x' ? 'left' : 'top';
    const endProp = axis === 'x' ? 'right' : 'bottom';
    const sizeKey = axis === 'x' ? 'clientWidth' : 'clientHeight';
    const scrollKey = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const containerSize = container[sizeKey];
    const containerScroll = container[scrollKey];

    const offsetStartInViewport =
        targetRect[startProp] - containerRect[startProp];
    const offsetStartInContainer = offsetStartInViewport + containerScroll;
    const targetSize = axis === 'x' ? target.clientWidth : target.clientHeight;

    const start = offsetStartInContainer;
    const end = offsetStartInContainer + targetSize - containerSize;
    const center = offsetStartInContainer + targetSize / 2 - containerSize / 2;

    switch (align) {
        case 'start':
            return Math.max(0, start);
        case 'end':
            return Math.max(0, end);
        case 'nearest':
            if (targetRect[endProp] > containerRect[endProp] + EPSILON_PX) {
                return Math.max(0, end);
            } else if (
                targetRect[startProp] <
                containerRect[startProp] - EPSILON_PX
            ) {
                return Math.max(0, start);
            }
            return containerScroll; // already fully visible on this axis
        case 'center':
        default:
            return Math.max(0, center);
    }
}
